import moment from 'moment';
import { getClients, addClient, updateClient, removeClient, inviteClient } from '../../Api/ApiClient';
import { getEmployCompany } from '../../Api/ApiUser';
import { setSnackbar } from '@/helpers';
import { clientUrl, postRequest } from '@/System/Store/endpoints';

const state = {
    sortOrder: 'ASC',
    editId: null,
    idUpdated: null,
    clients: [],
    edit: {},
    add: {
        organization_number: '',
        name: '',
        email: '',
        phone_number: '',
        postal_address: '',
        department: '',
        contact_person: '',
    },
    clientsUploadFile: null,
};

const mutations = {
    SORT_CLIENTS: (state, order) => {
        state.clients = state.clients.sort((a, b) => {
            if (order === 'asc') {
                return a.name.localeCompare(b.name);
            }
            if (order === 'desc') {
                return b.name.localeCompare(a.name);
            }
            return 0;
        });
    },
    SET_SORTED: (state, field) => {
        let sorted = [...state.clients];
        state.sortOrder = state.sortOrder === 'ASC' ? 'DESC' : 'ASC';

        if (field === 'name' || field === 'email' || field === 'organization_number' || field === 'phone_number') {
            // Default ASC
            sorted = state.clients.sort();

            if (state.sortOrder === 'DESC') {
                sorted = state.clients.sort().reverse();
            }
        } else if (field === 'created_at') {
            // Default ASC
            sorted = state.clients.sort((a, b) => (b.created_at < a.created_at ? 1 : -1));

            if (state.sortOrder === 'DESC') {
                sorted = state.clients.sort((a, b) => (b.created_at > a.created_at ? 1 : -1));
            }
        }

        state.clients = sorted;
    },
    SET_CLIENTS: (state, array) => {
        array.map((item) => {
            if (!item.created_at_display) {
                item.created_at_display = moment(item.created_at).format('D MMM YYYY');
            }
        });

        state.clients = array;
    },
    SET_ADD: (state, field) => {
        const keys = Object.keys(field);
        keys.forEach((key) => {
            state.add[key] = field[key];
        });
    },
    SET_RESET_ADD: (state) => {
        state.add = {
            id: null,
            organization_number: '',
            name: '',
            email: '',
            phone_number: '',
            postal_address: '',
            department: '',
            contact_person: '',
            invited_at: '',
        };
    },
    SET_EDIT_CLIENT: (state, field) => {
        const key = Object.keys(field)[0];
        state.edit[key] = field[key];
    },
    SET_EDIT_ID: (state, obj) => {
        if (obj.id === null) {
            state.edit = {};
        } else {
            if (obj.index === null) {
                const index = state.clients.findIndex((client) => client.id === obj.id);
                if (index >= 0) {
                    state.editId = index;
                    state.edit = state.clients[index];
                }
            } else {
                state.edit = state.clients[obj.index];
                state.edit.id = obj.id;
            }
        }
    },
    SET_UPDATED_CLIENT: (state, row) => {
        state.idUpdated = row.id;
    },
    UPDATE_CLIENTS_FILE: (state, file) => (state.clientsUploadFile = file),
};

const actions = {
    async GET_CLIENTS({ commit }) {
        return await getClients().then((response) => {
            if (response.status === 'Error') {
                return;
            }

            commit('SET_CLIENTS', response.data);
            commit('SORT_CLIENTS', 'asc');
            return state.clients;
        });
    },
    ADD_CLIENT: async ({ state, commit, dispatch }) => {
        commit('GlobalStore/SET_SAVE_STATUS', 'pending', { root: true });

        return await addClient(state.add).then((response) => {
            dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
            commit('GlobalStore/SET_SAVE_STATUS', response.status, { root: true });

            if (response.status === 'Error') {
                return false;
            }

            commit('SET_RESET_ADD');
            commit('SET_UPDATED_CLIENT', { id: response.data.id });

            commit('SET_CLIENTS', [...state.clients, { ...response.data, is_new: true }]);
            return true;
        });
    },
    UPDATE_CLIENT: async ({ state, commit, dispatch }) => {
        commit('GlobalStore/SET_SAVE_STATUS', 'pending', { root: true });

        return await updateClient(state.edit).then((response) => {
            commit('GlobalStore/SET_SAVE_STATUS', response.status, { root: true });

            if (response.status === 'Error') {
                dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
                return false;
            }

            commit('SET_UPDATED_CLIENT', response.data);

            return true;
        });
    },
    REMOVE_CLIENT: async ({ state, commit, dispatch }) => {
        await removeClient({ id: state.edit.id }).then((response) => {
            dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });

            if (response.status === 'Error') {
                return;
            }

            commit('SET_CLIENTS', response.data);
            commit('SET_EDIT_ID', {
                index: null,
                id: null,
            });
        });
    },
    INVITE_CLIENT: async ({ state, dispatch }) => {
        await inviteClient(state.edit).then((response) => {
            dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });

            if (response.status === 'Error') {
                return;
            }
        });
    },
    SORT_ARRAY: ({ commit }, field) => commit('SET_SORTED', field),
    UPLOAD_CLIENTS_FILE: async ({ commit, state, dispatch }) => {
        const formData = new FormData();
        const blob = await fetch(state.clientsUploadFile).then((res) => res.blob());

        formData.append('file', blob);

        postRequest(`${clientUrl}/multiple`, formData, {
            'Content-Type': 'multipart/form-data',
        })
            .then((response) => {
                if (response.status === 'Success') {
                    // Prepend the new clients to the existing list
                    const newClients = response.data.map((client) => ({ ...client, is_new: true }));
                    commit('SET_CLIENTS', [...state.clients, ...newClients]);
                }

                dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
            })
            .catch((error) => {
                dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(error), { root: true });
                commit('GlobalStore/SET_SAVE_STATUS', 'Error', { root: true });
            });
    },
    async REQUEST_COMPANY({ commit }, orgNr) {
        return await getEmployCompany({ organization_number: orgNr }).then((response) => {
            if (response.data && response.data.name) {
                let email = '';
                let phone_number = '';
                let postal_address = '';
                let contact_person = '';
                if (response.data.departments && response.data.departments.length > 0) {
                    let department = response.data.departments[0];
                    email = department.billing_email;
                    phone_number = department.billing_phone;
                    postal_address = (
                        department.billing_address +
                        ', ' +
                        department.billing_zipcode +
                        ' ' +
                        department.billing_city
                    ).trim();
                    if (postal_address.length < 4) postal_address = '';

                    if (department.certificate_meta.email && !email) {
                        email = department.certificate_meta.email;
                    }
                    if (department.certificate_meta.phone_number && !phone_number) {
                        phone_number = department.certificate_meta.phone_number;
                    }
                    if (department.certificate_meta.address && !postal_address) {
                        postal_address = department.certificate_meta.address;
                    }

                    if (department.user) {
                        let user = department.user;
                        if (!email && user.email) email = user.email;
                        if (!phone_number && user.phone_number) phone_number = user.phone_number;
                        contact_person = user.first_name + ' ' + user.last_name;
                    }
                }

                commit('SET_ADD', {
                    name: response.data.name,
                    organization_number: response.data.organization_number ?? orgNr,
                    email: email,
                    phone_number: phone_number,
                    postal_address: postal_address,
                    department: '',
                    contact_person: contact_person,
                });
            } else {
                commit('SET_ADD', {
                    organization_number: orgNr,
                });
            }
        });
    },
};

const getters = {
    GET_CLIENTS: (state) => state.clients,
    GET_CLIENT_BY_ID: (state) => (id) => state.clients.find((client) => client.id === id),
    GET_EDIT_ID: (state) => state.editId,
    GET_SORT_ORDER: (state) => state.sortOrder,
    GET_ID_UPDATED: (state) => state.idUpdated,
    GET_ADD_NAME: (state) => state.add.name,
    GET_ADD_ORG: (state) => state.add.organization_number,
    GET_ADD_DATA: (state) => state.add,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
